import type { IApiKey } from "@/features/tokens/types/token.types";
import type { HashKeyApiResponse } from "@/features/auth/types/hashKeys.types";
import { Button } from "@/components/ui/button";
import { ApiKeyGrid } from "./ApiKeyGrid";
import { ApiKeyGridSkeleton } from "./ApiKeyGridSkeleton";
import { useNavigate } from "@tanstack/react-router";
import { Spinner } from "@/components/ui/spinner";

interface HashKeyExpandedRowProps {
  hashKey: HashKeyApiResponse;
  allApiKeys: IApiKey[];
  selectedApiKeys: string[];
  keysToRemove: string[];
  isPending: boolean;
  isLoadingApiKeys: boolean;
  onToggle: (apiKeyId: string, isAlreadyDistributed: boolean) => void;
  onApplyChanges: () => void;
  onCollapse: () => void;
}

export function HashKeyExpandedRow({
  hashKey,
  allApiKeys,
  selectedApiKeys,
  keysToRemove,
  isPending,
  isLoadingApiKeys,
  onToggle,
  onApplyChanges,
  onCollapse,
}: HashKeyExpandedRowProps) {
  const navigate = useNavigate();
  const hasChanges = selectedApiKeys.length > 0 || keysToRemove.length > 0;

  return (
    <tr>
      <td colSpan={4} className="p-0 text-left">
        <div
          className="animate-in slide-in-from-top-2 mx-4 mb-4 space-y-5 rounded-lg p-5 duration-300"
          style={{
            background:
              "linear-gradient(#F8F8F8, #F8F8F8) padding-box, linear-gradient(to right, #FDBA74, #EA580C) border-box",
            border: "1px solid transparent",
            borderRadius: "8px",
          }}
        >
          {/* Hash Key Header */}
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 flex-col gap-2">
              <div className="flex items-center gap-3">
                <div className="text-sm font-bold text-neutral-950">
                  {hashKey.hash_key_name}
                </div>
                <span
                  className={`rounded-full px-2 py-1 text-xs font-medium ${
                    hashKey.active
                      ? "bg-green-500 text-white"
                      : "bg-red-500 text-white"
                  }`}
                >
                  {hashKey.active ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="text-xs text-neutral-500">
                {hashKey.hash_key_preview}
              </div>
            </div>

            <div className="flex flex-row gap-2">
              <Button
                className="text-muted hover:text-muted h-[32px] gap-2 bg-neutral-100 p-1.5 text-xs leading-normal font-bold tracking-normal"
                variant="minimal"
                icon
                onClick={() =>
                  navigate({
                    to: `/manager/subscription-management/edit-user/$hashKeyId`,
                    params: {
                      hashKeyId: hashKey.hash_key_id,
                    },
                    search: {
                      hashKeyId: hashKey.hash_key_id,
                    },
                  })
                }
              >
                <i className="ri-edit-line text-xs font-light"></i>
                Edit
              </Button>
              <Button
                className="text-muted hover:text-muted h-[32px] gap-2 bg-neutral-100 p-1.5 text-xs leading-normal font-bold tracking-normal"
                variant="minimal"
                icon
                onClick={onCollapse}
              >
                <i className="ri-arrow-left-line text-xs font-light"></i>
                Back
              </Button>
            </div>
          </div>

          {/* API Keys Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-neutral-800">
                API Key Distribution
              </h4>
              {hasChanges && (
                <Button
                  onClick={onApplyChanges}
                  disabled={isPending}
                  className="h-[32px] w-fit gap-2 text-xs"
                >
                  {isPending ? (
                    <div className="flex items-center gap-2">
                      <Spinner size="sm" className="stroke-white!" />
                      Applying...
                    </div>
                  ) : (
                    <>
                      <i className="ri-check-line text-sm" />
                      Apply Changes
                    </>
                  )}
                </Button>
              )}
            </div>

            {isLoadingApiKeys ? (
              <ApiKeyGridSkeleton />
            ) : allApiKeys.length === 0 ? (
              <div className="rounded-lg p-4 text-center text-sm text-neutral-500">
                No API keys available
              </div>
            ) : (
              <ApiKeyGrid
                allApiKeys={allApiKeys}
                currentApiKeys={hashKey.api_keys}
                selectedApiKeys={selectedApiKeys}
                keysToRemove={keysToRemove}
                isPending={isPending}
                isHashKeyActive={hashKey.active}
                onToggle={onToggle}
              />
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}
